'use client';

import { motion } from 'framer-motion';
import { AvatarWithFlair } from '@/components/prelaunch/avatar-with-flair';
import type { PlinthPreset } from '@/features/lobby/launch/data/presets';

function PlinthPlate({ plinth, width }: { plinth: PlinthPreset; width: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mt-3 flex flex-col items-center"
      style={{ width }}
    >
      <div
        className="h-2 w-full rounded-t-[3px]"
        style={{
          background: plinth.background,
          boxShadow: `inset 0 0 0 1px ${plinth.edge}66, 0 -1px 0 0 ${plinth.edge}40`,
        }}
      />
      <div
        className="h-2 w-full"
        style={{
          background: `linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.9))`,
          boxShadow: `0 10px 20px -8px rgba(0,0,0,0.9)`,
        }}
      />
    </motion.div>
  );
}

/**
 * Profile sidebar hero: avatar + flair ring, then plinth material as a short cross-section.
 * Used on public and connected profile views; reused in lobby onboarding.
 */
export function AvatarPlinthPreview({
  plinth,
  avatarUrl,
  address,
  level,
  editable,
  onEditClick,
}: {
  plinth: PlinthPreset | null;
  avatarUrl: string | null;
  address: string;
  level: number;
  editable?: boolean;
  onEditClick?: () => void;
}) {
  const accent = plinth?.edge ?? '#ffba49';
  return (
    // `shrink-0` is the desktop fix: this preview lives inside an
    // `overflow-y-auto flex flex-col` aside, where the default
    // `flex-shrink: 1` was letting the 240px hero collapse to almost
    // nothing whenever the sidebar's other rows (header + Socials +
    // leaderboard snippet) totalled more than the modal column —
    // visually the avatar/plinth/level had "lost its height". Pinning
    // shrink to 0 keeps the hero at its design height and lets the
    // aside scrollbar take over when stacked content exceeds the
    // column. `min-h-[240px]` belt-and-braces this on browsers that
    // honour explicit `height:` over `flex-shrink`.
    //
    // The two decorative layers below (`blur-2xl` halo +
    // `mix-blend-overlay` noise) were the dominant cost on mobile
    // scroll — every scroll frame had to repaint the blurred halo
    // because the parent dialog is also a scroll surface and Mobile
    // Safari can't promote nested blurs to their own layer. Dropping
    // them on `<sm` keeps the tone (gradient base + accent halo) but
    // removes the per-frame GPU hit; desktop keeps the legacy treatment.
    <div
      // Mobile drops the hero from 240→180px so the wallet-actions
      // row + leaderboard snippet below sit closer to the top of the
      // scroll surface — on a typical phone the user previously had
      // to scroll past the entire 240px hero AND the socials block
      // before reaching either, which made the connected-wallet
      // disconnect/claim CTAs feel hidden. Desktop keeps the full
      // 240px size unchanged.
      className="relative overflow-hidden h-[180px] min-h-[180px] sm:h-[240px] sm:min-h-[240px] shrink-0 group/preview"
      style={{
        background:
          'radial-gradient(ellipse at 50% 25%, rgba(255,186,73,0.12) 0%, rgba(10,8,7,0.9) 50%, #040304 100%)',
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(circle at 50% 45%, ${accent}33, transparent 65%)`,
          opacity: plinth ? 0.6 : 0.35,
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay hidden sm:block"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.25) 1px, transparent 1px)',
          backgroundSize: '3px 3px',
        }}
      />
      <div className="relative h-full flex flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col items-center"
        >
          <div className="relative">
            <div
              aria-hidden
              className="absolute -inset-6 rounded-full blur-2xl hidden sm:block"
              style={{ background: `radial-gradient(circle, ${accent}44, transparent 70%)` }}
            />
            {editable && onEditClick ? (
              <button
                type="button"
                onClick={onEditClick}
                className="relative rounded-full cursor-pointer group-hover/preview:ring-2 group-hover/preview:ring-amber-400/40"
              >
                <AvatarWithFlair avatarUrl={avatarUrl} address={address} level={level} size="xl" />
                <span className="pointer-events-none absolute inset-0 rounded-full bg-black/0 group-hover/preview:bg-black/35 transition-colors" />
              </button>
            ) : (
              <AvatarWithFlair avatarUrl={avatarUrl} address={address} level={level} size="xl" />
            )}
          </div>
          {plinth && <PlinthPlate plinth={plinth} width={172} />}
        </motion.div>
        {plinth && (
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between px-1">
            <span className="text-[9px] font-mono tracking-[0.22em] uppercase text-amber-100/70">
              {plinth.name}
            </span>
            <span className="text-[9px] font-mono tracking-[0.18em] uppercase text-zinc-500">
              Lvl {level}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
